import { useState, useEffect, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, apiPost, Cargando, ErrorApi } from '../shared/ui.tsx';
import { SelectorHorario } from '../shared/SelectorHorario.tsx';
import { ListaPersonasTurno, type PersonaDisponible } from '../shared/ListaPersonasTurno.tsx';
import { ResumenPedido, type ResumenTurnoItem } from '../shared/ResumenPedido.tsx';
import { formatoRango } from '../shared/horas.ts';
import { apiFetch } from '../shared/api.ts';
import type { Categoria, Salon, Cliente } from '../shared/tipos.ts';

/** Un turno pedido dentro de una categoría (0018). horario null = hereda el del evento. */
interface TurnoForm {
  key: string;
  cantidad: number;
  horario: { ini: string; fin: string } | null;
  sel: string[];
}

function Icono({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const iconCats: Record<string, string> = {
  cocinero: 'M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z',
  mozo: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0',
  default: 'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z',
};

export function NuevaSolicitudAdminPage(): ReactNode {
  const navigate = useNavigate();
  const clientes = useApi<Cliente[]>('/api/clientes');
  const categorias = useApi<Categoria[]>('/api/categorias?activo=1');

  const [clienteId, setClienteId] = useState('');
  const [salones, setSalones] = useState<Salon[]>([]);
  const [cargandoSalones, setCargandoSalones] = useState(false);
  const [salonId, setSalonId] = useState('');
  const [fecha, setFecha] = useState('');
  const [horaInicio, setHoraInicio] = useState('20:00');
  const [horaFin, setHoraFin] = useState('23:59');
  const [descripcion, setDescripcion] = useState('');
  // 0018: cada categoría puede tener VARIOS turnos en el mismo evento
  // (ej. 4 mozos 20:00 y otros 4 a las 02:00). horario null = hereda el del evento.
  const [turnosCat, setTurnosCat] = useState<Record<string, TurnoForm[]>>({});
  const [expandida, setExpandida] = useState<string | null>(null);
  const [personasCache, setPersonasCache] = useState<Record<string, PersonaDisponible[]>>({});
  const [cargandoPersonas, setCargandoPersonas] = useState<Record<string, boolean>>({});
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hoy = new Date().toISOString().slice(0, 10);
  const esUrgencia = fecha && (new Date(fecha).getTime() - Date.now()) < 48 * 3600 * 1000;
  const totalPuestos = Object.values(turnosCat).flat().reduce((a, t) => a + t.cantidad, 0);
  const selGlobal = new Set(Object.values(turnosCat).flat().flatMap((t) => t.sel));

  const claveDisp = `${fecha}|${horaInicio}|${horaFin}`;
  function turnosDe(catId: string): TurnoForm[] { return turnosCat[catId] ?? []; }
  function cantidadCat(catId: string): number { return turnosDe(catId).reduce((a, t) => a + t.cantidad, 0); }
  function selCat(catId: string): number { return turnosDe(catId).reduce((a, t) => a + t.sel.length, 0); }
  function horarioTurno(t: TurnoForm): { ini: string; fin: string } { return t.horario ?? { ini: horaInicio, fin: horaFin }; }
  function claveTurno(catId: string, t: TurnoForm): string {
    const h = horarioTurno(t);
    return `${catId}|${fecha}|${h.ini}|${h.fin}`;
  }

  useEffect(() => {
    // Cambió fecha u horario del evento: las selecciones se hicieron contra otra disponibilidad.
    setTurnosCat((m) => Object.fromEntries(Object.entries(m).map(([k, ts]) => [k, ts.map((t) => ({ ...t, sel: [] }))])));
  }, [claveDisp]);

  const claveTurnos = JSON.stringify(Object.entries(turnosCat).map(([k, ts]) => [k, ts.map((t) => t.horario)]));
  useEffect(() => {
    if (!expandida || !fecha || !horaInicio || !horaFin) return;
    for (const t of turnosDe(expandida)) {
      const clave = claveTurno(expandida, t);
      if (!personasCache[clave] && !cargandoPersonas[clave]) cargarDisponibles(expandida, t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandida, claveDisp, claveTurnos]);

  function setTurnos(catId: string, ts: TurnoForm[]) {
    setTurnosCat((m) => ({ ...m, [catId]: ts }));
  }

  function nuevoTurno(cantidad: number, horario: { ini: string; fin: string } | null): TurnoForm {
    return { key: crypto.randomUUID(), cantidad, horario, sel: [] };
  }

  // Botones +/- del encabezado de la categoría: operan sobre el primer turno.
  function setCat(catId: string, delta: number) {
    const ts = turnosDe(catId);
    if (ts.length === 0) {
      if (delta > 0) setTurnos(catId, [nuevoTurno(1, null)]);
      return;
    }
    cambiarCantidadTurno(catId, ts[0].key, delta);
  }

  function cambiarCantidadTurno(catId: string, key: string, delta: number) {
    const ts = turnosDe(catId)
      .map((t) => {
        if (t.key !== key) return t;
        const cantidad = Math.max(0, Math.min(50, t.cantidad + delta));
        return { ...t, cantidad, sel: t.sel.slice(0, cantidad) };
      })
      .filter((t) => t.cantidad > 0);
    setTurnos(catId, ts);
  }

  function agregarTurno(catId: string) {
    setTurnos(catId, [...turnosDe(catId), nuevoTurno(1, { ini: horaInicio, fin: horaFin })]);
    setExpandida(catId);
  }

  function quitarTurno(catId: string, key: string) {
    setTurnos(catId, turnosDe(catId).filter((t) => t.key !== key));
  }

  function toggleHorarioTurno(catId: string, key: string) {
    setTurnos(catId, turnosDe(catId).map((t) =>
      t.key === key ? { ...t, horario: t.horario ? null : { ini: horaInicio, fin: horaFin }, sel: [] } : t,
    ));
  }

  function cambiarHorarioTurno(catId: string, key: string, ini: string, fin: string) {
    setTurnos(catId, turnosDe(catId).map((t) => (t.key === key ? { ...t, horario: { ini, fin }, sel: [] } : t)));
  }

  function togglePersona(catId: string, key: string, personaId: string) {
    setTurnos(catId, turnosDe(catId).map((t) => {
      if (t.key !== key) return t;
      if (t.sel.includes(personaId)) return { ...t, sel: t.sel.filter((id) => id !== personaId) };
      if (t.sel.length >= t.cantidad) return t;
      return { ...t, sel: [...t.sel, personaId] };
    }));
  }

  // Resumen para revisar antes de crear: elegidas por nombre + autos por turno.
  function armarResumen(): ResumenTurnoItem[] {
    return (categorias.datos ?? []).flatMap((c) => {
      const ts = turnosDe(c.id);
      return ts.map((t, i) => {
        const h = horarioTurno(t);
        const personas = personasCache[claveTurno(c.id, t)] ?? [];
        return {
          categoria: c.nombre,
          numeroTurno: ts.length === 1 ? null : i + 1,
          rango: formatoRango(h.ini, h.fin),
          elegidas: t.sel.map((id) => {
            const p = personas.find((x) => x.id === id);
            return p ? `${p.apellido}, ${p.nombre}` : 'Elegida a dedo';
          }),
          autos: t.cantidad - t.sel.length,
        };
      });
    });
  }

  async function cambiarCliente(id: string) {
    setClienteId(id);
    setSalonId('');
    setSalones([]);
    if (id) {
      setCargandoSalones(true);
      try {
        const data = await apiFetch<{ salones: Salon[] }>(`/api/clientes/${id}`);
        setSalones(data.salones ?? []);
      } catch {
        setSalones([]);
      } finally {
        setCargandoSalones(false);
      }
    }
  }

  function expandirCategoria(catId: string) {
    setExpandida(expandida === catId ? null : catId);
  }

  function cargarDisponibles(catId: string, t: TurnoForm) {
    const h = horarioTurno(t);
    const clave = claveTurno(catId, t);
    setCargandoPersonas((m) => ({ ...m, [clave]: true }));
    apiFetch<{ personas: PersonaDisponible[] }>(
      `/api/personal/disponibles?categoria_id=${catId}&fecha=${fecha}&hora_inicio=${h.ini}&hora_fin=${h.fin}`,
    )
      .then((datos) => setPersonasCache((m) => ({ ...m, [clave]: datos.personas })))
      .catch(() => { /* ignorar */ })
      .finally(() => setCargandoPersonas((m) => ({ ...m, [clave]: false })));
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!clienteId) { setErr('Elegí un cliente'); return; }
    if (!salonId) { setErr('Elegí un salón'); return; }
    if (!fecha) { setErr('Elegí una fecha'); return; }
    if (horaFin === horaInicio) { setErr('La hora de fin no puede ser igual a la de inicio'); return; }
    if (totalPuestos === 0) { setErr('Pedí al menos 1 persona'); return; }

    setEnviando(true);
    try {
      const puestos = Object.entries(turnosCat).flatMap(([categoria_id, ts]) =>
        ts.filter((t) => t.cantidad > 0).map((t) => ({
          categoria_id,
          cantidad: t.cantidad,
          personas_seleccionadas: t.sel,
          hora_inicio: t.horario?.ini,
          hora_fin: t.horario?.fin,
        })),
      );
      await apiPost('/api/solicitudes', {
        cliente_id: clienteId,
        salon_id: salonId,
        fecha,
        hora_inicio: horaInicio,
        hora_fin: horaFin,
        descripcion,
        puestos,
      });
      navigate('/admin/solicitudes', { replace: true });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al enviar');
    } finally {
      setEnviando(false);
    }
  }

  if (clientes.cargando || categorias.cargando) return <Cargando />;
  if (clientes.error) return <ErrorApi mensaje={clientes.error} onReintentar={clientes.recargar} />;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Nueva solicitud</h2>
        <p className="text-sm text-neutro-500 mt-0.5">Creá una solicitud en nombre de un cliente</p>
      </div>

      <form onSubmit={enviar} className="space-y-5">
        {/* Cliente + Salón */}
        <div className="rounded-2xl border border-contorno-var bg-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primario-100">
              <Icono d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" className="h-5 w-5 text-primario-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-neutro-800">Cliente y salón</h3>
              <p className="text-xs text-neutro-400">Elegí el cliente y el salón del evento</p>
            </div>
          </div>
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Cliente *</span>
              <select className="campo" required value={clienteId} onChange={(e) => cambiarCliente(e.target.value)}>
                <option value="">Elegí un cliente…</option>
                {clientes.datos?.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </label>
            {clienteId && (
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Salón *</span>
                {cargandoSalones ? (
                  <Cargando texto="Cargando salones…" />
                ) : (
                  <select className="campo" required value={salonId} onChange={(e) => setSalonId(e.target.value)}>
                    <option value="">Elegí un salón…</option>
                    {salones.map((s) => (
                      <option key={s.id} value={s.id}>{s.nombre}{s.direccion ? ` · ${s.direccion}` : ''}</option>
                    ))}
                  </select>
                )}
              </label>
            )}
          </div>
        </div>

        {/* Fecha y horario */}
        <div className="rounded-2xl border border-contorno-var bg-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primario-100">
              <Icono d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" className="h-5 w-5 text-primario-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-neutro-800">Datos del evento</h3>
              <p className="text-xs text-neutro-400">Fecha y horario del servicio</p>
            </div>
          </div>
          <div className="space-y-4">
            <label className="block max-w-[220px]">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Fecha *</span>
              <input className="campo" type="date" min={hoy} required value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </label>
            <SelectorHorario
              horaInicio={horaInicio}
              horaFin={horaFin}
              onChange={(ini, fin) => { setHoraInicio(ini); setHoraFin(fin); }}
              colorActivo="bg-primario-400"
            />
          </div>
          {esUrgencia && fecha && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-xs font-medium text-amber-700">Pedido de urgencia (menos de 48h): sujeto a disponibilidad.</p>
            </div>
          )}
        </div>

        {/* Personal */}
        <div className="rounded-2xl border border-contorno-var bg-white p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primario-100">
              <Icono d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" className="h-5 w-5 text-primario-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-neutro-800">Personal que necesitás</h3>
              <p className="text-xs text-neutro-400">Seleccioná categorías y personas</p>
            </div>
          </div>
          <div className="space-y-1">
            {categorias.datos?.map((c) => {
              const cant = cantidadCat(c.id);
              const nSel = selCat(c.id);
              const expandidaAqui = expandida === c.id;
              const catIcon = iconCats[c.nombre.toLowerCase()] ?? iconCats['default'];
              return (
                <div key={c.id} className={`acordeon-item rounded-xl transition-colors ${expandidaAqui ? 'bg-superficie' : ''}`} data-active={expandidaAqui}>
                  <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-4">
                    <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left text-sm text-neutro-700 hover:text-primario-400 transition-colors sm:gap-3" onClick={() => expandirCategoria(c.id)} disabled={cant === 0}>
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${cant > 0 ? 'bg-primario-100' : 'bg-neutro-100'}`}>
                        <Icono d={catIcon} className={`h-4 w-4 ${cant > 0 ? 'text-primario-400' : 'text-neutro-400'}`} />
                      </div>
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{c.nombre}</span>
                        {(turnosDe(c.id).length > 1 || (cant > 0 && nSel > 0)) && (
                          <span className="mt-0.5 flex flex-wrap items-center gap-1">
                            {turnosDe(c.id).length > 1 && (
                              <span className="badge badge-info">{turnosDe(c.id).length} turnos</span>
                            )}
                            {cant > 0 && nSel > 0 && (
                              <span className="badge badge-info">{nSel} elegida{nSel !== 1 ? 's' : ''}</span>
                            )}
                          </span>
                        )}
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                      <button type="button" className="flex h-7 w-7 items-center justify-center rounded-full border border-contorno-var bg-white text-lg font-medium text-neutro-600 hover:bg-superficie-100 disabled:opacity-30 transition-colors sm:h-8 sm:w-8" onClick={() => setCat(c.id, -1)} disabled={!cant}>−</button>
                      <span className="w-6 text-center text-sm font-semibold text-neutro-800 sm:w-7">{cant}</span>
                      <button type="button" className="flex h-7 w-7 items-center justify-center rounded-full bg-primario-400 text-lg font-medium text-white hover:bg-primario-500 transition-colors sm:h-8 sm:w-8" onClick={() => setCat(c.id, 1)}>+</button>
                    </div>
                  </div>
                  {expandidaAqui && cant > 0 && (
                    <div className="px-4 pb-4 pt-0 ml-10 space-y-3">
                      {!fecha || !horaInicio || !horaFin ? (
                        <p className="text-xs text-neutro-400">Elegí fecha y horario para ver disponibilidad.</p>
                      ) : (
                        <>
                          {turnosDe(c.id).map((t, i) => {
                            const clave = claveTurno(c.id, t);
                            const personas = personasCache[clave];
                            const h = horarioTurno(t);
                            const esUnico = turnosDe(c.id).length === 1;
                            return (
                              <div key={t.key} className="rounded-xl border border-contorno-var bg-white p-3">
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                  <span className="text-sm font-bold text-neutro-800">
                                    {esUnico ? 'Turno' : `Turno ${i + 1}`}
                                    <span className="ml-1.5 text-xs font-semibold text-primario-400">{formatoRango(h.ini, h.fin)}</span>
                                  </span>
                                  <span className="flex items-center gap-2">
                                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded-full border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 transition-colors" onClick={() => cambiarCantidadTurno(c.id, t.key, -1)}>−</button>
                                    <span className="w-6 text-center text-sm font-semibold">{t.cantidad}</span>
                                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded-full bg-primario-400 text-white hover:bg-primario-500 transition-colors" onClick={() => cambiarCantidadTurno(c.id, t.key, 1)}>+</button>
                                    {!esUnico && (
                                      <button type="button" className="ml-1 text-xs font-semibold text-estado-error hover:underline" onClick={() => quitarTurno(c.id, t.key)}>✕ Quitar</button>
                                    )}
                                  </span>
                                </div>
                                <label className="mb-2 flex items-center gap-2 text-xs font-semibold text-neutro-600">
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-neutro-300 text-primario-400 focus:ring-primario-400"
                                    checked={!!t.horario}
                                    onChange={() => toggleHorarioTurno(c.id, t.key)}
                                  />
                                  Horario propio para este turno
                                </label>
                                {t.horario && (
                                  <div className="mb-3 rounded-xl border border-contorno-var bg-superficie-100/40 p-3">
                                    <SelectorHorario
                                      horaInicio={t.horario.ini}
                                      horaFin={t.horario.fin}
                                      onChange={(ini, fin) => cambiarHorarioTurno(c.id, t.key, ini, fin)}
                                      colorActivo="bg-primario-400"
                                    />
                                  </div>
                                )}
                                {cargandoPersonas[clave] ? (
                                  <Cargando texto="Buscando personas…" />
                                ) : personas ? (
                                  <ListaPersonasTurno
                                    personas={personas}
                                    sel={t.sel}
                                    cantidad={t.cantidad}
                                    selGlobal={selGlobal}
                                    onToggle={(pid) => togglePersona(c.id, t.key, pid)}
                                  />
                                ) : (
                                  <button type="button" className="text-xs font-medium text-primario-400 hover:text-primario-500" onClick={() => cargarDisponibles(c.id, t)}>Cargar personas</button>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="text-xs font-semibold text-primario-400 hover:underline"
                            onClick={() => agregarTurno(c.id)}
                          >
                            ＋ Agregar otro turno con distinto horario
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {totalPuestos > 0 && <ResumenPedido items={armarResumen()} total={totalPuestos} />}
        </div>

        {/* Descripción */}
        <div className="rounded-2xl border border-contorno-var bg-white p-5">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Descripción (opcional)</span>
            <textarea className="campo" rows={2} placeholder="Casamiento 180 pax, servicio emplatado…" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </label>
        </div>

        {err && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm font-medium text-red-700">{err}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/admin/solicitudes')} className="flex-1 boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100">
            Cancelar
          </button>
          <button type="submit" disabled={enviando} className="flex-1 boton bg-primario-400 text-white hover:bg-primario-500 shadow-sm">
            {enviando ? 'Enviando…' : 'Crear solicitud'}
          </button>
        </div>
      </form>
    </div>
  );
}
